/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useCalendarGrid, useDateFormatter, useLocale } from 'react-aria';
import { type CalendarState, type RangeCalendarState } from 'react-stately';
import { type DateValue, endOfMonth, getWeeksInMonth } from '@internationalized/date';
import cx from 'classnames';

import { Heading } from 'sarsaparilla';

import { CalendarCell } from './CalendarCell';
import type { CalendarLayout } from './date-picker-types';

interface CalendarGridProps {
    state: RangeCalendarState | CalendarState;
    offset?: { months?: number };
    /** Set to false when the weekday labels are rendered once outside the grids */
    showWeekDays?: boolean;
    layout?: CalendarLayout;
}

/** Identifies a month's grid so the calendar can find it in the DOM. */
export function getMonthKey(date: DateValue) {
    return `${date.year}-${date.month}`;
}

export function CalendarGrid({
    state,
    offset = {},
    showWeekDays = true,
    layout = 'paged',
}: CalendarGridProps) {
    const { locale } = useLocale();
    const startDate = state.visibleRange.start.add(offset);
    const endDate = endOfMonth(startDate);
    const { gridProps, headerProps, weekDays } = useCalendarGrid(
        { startDate, endDate },
        state
    );

    const monthDateFormatter = useDateFormatter({
        month: 'long',
        year: 'numeric',
        era:
            startDate.calendar.identifier === 'gregory' && startDate.era === 'BC'
                ? 'short'
                : undefined,
        calendar: startDate.calendar.identifier,
        timeZone: state.timeZone,
    });

    // Get the number of weeks in the month so we can render the proper number of rows.
    const weeksInMonth = getWeeksInMonth(startDate, locale);

    return (
        <div className="calendar-grid" data-month={getMonthKey(startDate)}>
            <Heading
                // We have a visually hidden heading describing the entire visible range,
                // and the calendar itself describes the individual month
                // so we don't need to repeat that here for screen reader users.
                aria-hidden
                appearance="h6"
                headingLevel={2}
                className={cx('heading', { centered: layout === 'paged' })}
            >
                {monthDateFormatter.format(startDate.toDate(state.timeZone))}
            </Heading>

            <div {...gridProps} className="calendar-table">
                {showWeekDays ? (
                    <div {...headerProps}>
                        <div className="tr">
                            {weekDays.map((day, index) => (
                                <div className="th" key={index}>
                                    {day}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}
                <div role="rowgroup">
                    {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
                        <div role="row" className="tr" key={weekIndex}>
                            {state.getDatesInWeek(weekIndex, startDate).map((date, i) => {
                                return date ? (
                                    <CalendarCell
                                        key={i}
                                        state={state}
                                        date={date}
                                        currentMonth={startDate}
                                    />
                                ) : (
                                    <div className="td" key={i} />
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
